import React, { useEffect } from 'react';
import AudioLevelMeter from './AudioLevelMeter';

export interface HostMicrophoneControlData {
  userId: string;
  displayName: string;
  startedAt: number;
  stream: MediaStream | null;
  analyser?: AnalyserNode | null;
  level?: number;
  peak?: number;
  volume: number; // 0-100 slider value
  muted: boolean;
  pan: number; // -1 to 1
  connectionState?: string;
  reconnectStatus?: 'idle' | 'pending' | 'sent' | 'succeeded' | 'failed';
}

interface HostMicrophoneControlProps {
  data: HostMicrophoneControlData;
  onVolumeChange: (userId: string, volume: number) => void;
  onMuteToggle: (userId: string) => void;
  onPanChange: (userId: string, pan: number) => void;
  onDisconnect: (userId: string) => void;
  onTrackToggle: (userId: string, enabled: boolean) => void; // New prop for toggling track
  onReconnect?: (userId: string) => void;
}

const formatElapsed = (startedAt: number) => {
  const seconds = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${remainder.toString().padStart(2, '0')}`;
};

const HostMicrophoneControl: React.FC<HostMicrophoneControlProps> = ({
  data,
  onVolumeChange,
  onMuteToggle,
  onPanChange,
  onDisconnect,
  onTrackToggle,
  onReconnect,
}) => {
  const { userId, displayName, startedAt, stream, analyser, level, peak, volume, muted, pan, connectionState, reconnectStatus } = data;

  useEffect(() => {
    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !muted; // Enable or disable track based on mute state
      }
    }
  }, [stream, muted]);

  return (
    <div className="bg-black/40 border border-white/10 rounded-xl p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
            <div className="flex items-center gap-2">
              <h4 className="text-white font-semibold text-sm">{displayName}</h4>
              {reconnectStatus && reconnectStatus !== 'idle' && (
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                  reconnectStatus === 'pending' || reconnectStatus === 'sent' ? 'bg-yellow-600 text-black' : ''
                } ${reconnectStatus === 'succeeded' ? 'bg-green-600 text-white' : ''} ${reconnectStatus === 'failed' ? 'bg-red-600 text-white' : ''}`}>
                  {reconnectStatus}
                </span>
              )}
            </div>
          <p className="text-gray-400 text-xs">Connected {formatElapsed(startedAt)} ago</p>
          {connectionState && (
            <p className="text-[11px] text-gray-500 mt-1">State: {connectionState}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              onMuteToggle(userId);
              onTrackToggle(userId, muted); // Toggle track when mute state changes
            }}
            className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${
              muted
                ? 'bg-red-600/80 text-white hover:bg-red-500'
                : 'bg-emerald-600/80 text-white hover:bg-emerald-500'
            }`}
          >
            {muted ? 'Unmute' : 'Mute'}
          </button>
          <button
            onClick={() => onDisconnect(userId)}
            className="px-3 py-1 rounded text-xs font-semibold bg-gray-700/70 hover:bg-gray-600 text-gray-100 transition-colors"
          >
            Disconnect
          </button>
            <button
              onClick={() => onReconnect && onReconnect(userId)}
              className="px-3 py-1 rounded text-xs font-semibold bg-yellow-700/70 hover:bg-yellow-600 text-white transition-colors"
            >
              Reconnect
            </button>
        </div>
      </div>

      <AudioLevelMeter
        stream={stream}
        analyser={analyser ?? undefined}
        levelValue={level}
        peakValue={peak}
        label="Live Level"
        className="bg-white/5 border border-white/10 rounded-md p-3"
        colorClass={muted ? 'bg-red-400' : 'bg-emerald-400'}
      />

      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="text-gray-300 text-xs w-16">Volume</span>
          <input
            type="range"
            min={0}
            max={100}
            value={volume}
            onChange={(event) => onVolumeChange(userId, Number(event.target.value))}
            className="flex-1 accent-emerald-400"
          />
          <span className="text-gray-200 text-xs font-semibold w-12 text-right">{volume}%</span>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-gray-300 text-xs w-16">Balance</span>
          <input
            type="range"
            min={-100}
            max={100}
            value={Math.round(pan * 100)}
            onChange={(event) => onPanChange(userId, Number(event.target.value) / 100)}
            className="flex-1 accent-blue-400"
          />
          <span className="text-gray-200 text-xs font-semibold w-12 text-right">
            {pan < 0 ? `${Math.abs(Math.round(pan * 100))}% L` : pan > 0 ? `${Math.round(pan * 100)}% R` : 'Center'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default HostMicrophoneControl;
